// Authentication Context for Heapster ID
// Provides authentication state management across the application
// Aligns with PRD Section 5.1 (Authentication & Authorization) and Section 8 (Data Model)

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import {
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';

// ActionCodeSettings interface for email verification
interface ActionCodeSettings {
  url: string;
  handleCodeInApp?: boolean;
  iOS?: {
    bundleId: string;
  };
  android?: {
    packageName: string;
    installApp?: boolean;
    minimumVersion?: string;
  };
  linkDomain?: string;
}
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { appConfig } from '../lib/env';
import type { UserProfile, PhotoUrls } from '../lib/types/user';
import { DisplayNameSource } from '../lib/types/user';
import {
  measureAsync,
  recordUserFlow,
  UserFlowMetrics,
} from '../lib/performance';
import { isPRPreviewEnvironment } from '../lib/utils/environment';
import {
  AccountCreatedSignInFailedError,
  SignUpRequestError,
} from '../lib/auth/signUpError';

// Enhanced user interface that combines Firebase User with Heapster profile data
export interface HeapsterUser
  extends Omit<UserProfile, 'created_at' | 'email'> {
  // Firebase user properties
  email: string | null;
  emailVerified: boolean;
  // Heapster-specific properties (from PRD Section 8)
  uid: string;
  display_name: string;
  photo_url?: PhotoUrls | null;
  created_at: Date;
  // Profile completion status (PRD Section 5.2)
  profile_completed: boolean;
}

// Authentication state interface
export interface AuthState {
  user: HeapsterUser | null;
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
  isEmailVerified: boolean;
  profileCompleted: boolean;
  isNewUser: boolean;
  onboardingStep:
    | 'email-verification'
    | 'profile-completion'
    | 'complete'
    | null;
}

// Authentication actions interface
export interface AuthActions {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    accessCode?: string
  ) => Promise<void>;
  signOut: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
}

// Combined context interface
export interface AuthContextType extends AuthState, AuthActions {}

// Create context with undefined default (will throw error if used outside provider)
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<HeapsterUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);

  // Email verification configuration - ensures links redirect to our custom domain
  const createActionCodeSettings = (userEmail: string): ActionCodeSettings => {
    const appUrl = import.meta.env.VITE_APP_URL;

    if (!appUrl) {
      throw new Error(
        'VITE_APP_URL environment variable is required for email verification. ' +
          'Please set VITE_APP_URL to your application domain (e.g., https://dev.heapster.id). ' +
          'This should be configured in your deployment environment or GitHub Actions.'
      );
    }

    return {
      url: `${appUrl}/auth/verify-complete?email=${encodeURIComponent(userEmail)}`,
      // Inert here, and the comment it replaces was wrong: it read "pass action
      // codes to our app for manual processing", which is not what happens.
      // `handleCodeInApp` governs whether the link opens in a MOBILE APP first,
      // and needs an `iOS` or `android` block to have anything to open. With
      // neither, the link goes to Firebase's hosted handler either way — which
      // applies the code itself, for verifyEmail only once the user presses its
      // "Complete verification" button (#2071), and then offers a Continue
      // button pointing at `url`. Left in place rather than removed because
      // nothing depends on it and removing it is a behaviour change to argue
      // separately; corrected because the old comment invites building a page
      // that waits for an `oobCode` that never arrives (found while planning
      // #1831).
      handleCodeInApp: true,
    };
  };

  // Determine onboarding step based on user state
  const getOnboardingStep = (
    user: HeapsterUser | null,
    firebaseUser: FirebaseUser | null
  ): 'email-verification' | 'profile-completion' | 'complete' | null => {
    if (!user || !firebaseUser) return null;

    if (!firebaseUser.emailVerified) {
      return 'email-verification';
    }

    if (!user.profile_completed) {
      return 'profile-completion';
    }

    return 'complete';
  };

  // Load user profile from Firestore
  const loadUserProfile = async (
    firebaseUser: FirebaseUser
  ): Promise<HeapsterUser | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData) {
          return {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified,
            email_verified:
              userData.email_verified ?? firebaseUser.emailVerified,
            display_name:
              userData.display_name || firebaseUser.displayName || '',
            display_name_source:
              userData.display_name_source || DisplayNameSource.AUTO,
            photo_url: userData.photo_url || firebaseUser.photoURL || undefined,
            created_at: userData.created_at?.toDate() || new Date(),
            profile_completed:
              userData.profile_completed ??
              !!(userData.display_name || firebaseUser.displayName),
          };
        }
      }

      // If no user document exists, create a basic user object from Firebase auth
      // The beforeUserSignedIn Cloud Function will create the profile on next sign-in
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified,
        email_verified: firebaseUser.emailVerified,
        display_name: firebaseUser.displayName || '',
        display_name_source: DisplayNameSource.AUTO,
        photo_url: undefined,
        created_at: firebaseUser.metadata.creationTime
          ? new Date(firebaseUser.metadata.creationTime)
          : new Date(),
        profile_completed: false,
      };
    } catch (error) {
      console.error('Error loading user profile:', error);
      return null;
    }
  };

  // Refresh user profile data
  //
  // A `null` from `loadUserProfile` means the READ FAILED — it is returned only
  // from that function's catch, and a genuinely missing document yields a
  // synthesised profile rather than null. So writing it into state would turn a
  // transient Firestore blip into a silent logout: `ProtectedRoute` redirects on
  // `!user`, and the person is dropped on the landing page mid-session with no
  // error and nothing to retry. Keep what we have and let the next refresh sort
  // it out.
  const refreshUserProfile = async (): Promise<void> => {
    if (firebaseUser) {
      const profile = await loadUserProfile(firebaseUser);
      if (profile) {
        setUser(profile);
      }
    }
  };

  // Sign in with email and password
  // `isLoading` is NOT set at the start of this function, for the same reason
  // it is not set at the start of `signUp` below (#1950).
  //
  // `PublicRoute` renders a full-screen spinner INSTEAD of its children while
  // `isLoading`, so setting it here unmounted `SignInPage` for the duration of
  // the attempt. On a rejected credential the `finally` cleared it again,
  // `PublicRoute` mounted a FRESH `SignInPage`, and the `setGlobalError` in its
  // catch had written to the instance React had just thrown away. The user
  // pressed Sign In, watched a spinner flash, and got the form back with no
  // error on it at all — silence being worse than the vague message it
  // replaced, since nothing even confirmed the attempt had failed. The focus
  // return to the email field was lost the same way.
  //
  // Verified against live dev, not inferred: the message was computed and
  // logged to the console while `#form-error` was absent from the DOM.
  //
  // `SignInPage` has its own pending state (`signInMutation.isPending`) for
  // disabling the form, so nothing here needs `isLoading` on the way in.
  //
  // Nor on the way out — and `isLoading` in particular is never written here
  // again, which is the whole repair. `onAuthStateChanged` below is its only
  // writer, and a successful credential is what makes that listener fire: it
  // raises the flag, loads the profile, sets the user and lowers the flag —
  // the entire transition, already implemented.
  //
  // Raising it here as well looks harmless and is not, because the two run
  // concurrently. Measured, not reasoned about: firing the observer the way the
  // real SDK does — from inside the credential's resolution — leaves
  // `isLoading` STUCK TRUE whenever the observer finishes first. It lowers the
  // flag, this function raises it again, and nothing remains to lower it, so
  // `PublicRoute` shows its spinner instead of children forever and a CORRECT
  // password strands the user on a blank loading screen. The old
  // `finally { setIsLoading(false) }` was hiding that race at the cost of the
  // bug this fix is for.
  //
  // `setUser`, unlike `isLoading`, does still belong here, for a case the
  // observer cannot cover. Firebase gates the auth-state callback on
  // `lastNotifiedUid !== currentUid`, so signing in AS THE UID ALREADY SIGNED
  // IN fires nothing. That state is reachable and documented in
  // `OidcContinuePage` and `OidcLogoutPage`: a failed profile read leaves
  // `user` null while the Firebase session is very much alive, `PublicRoute`
  // then shows the sign-in form to somebody already signed in, and their
  // correct password would resolve into silence — the same do-nothing failure
  // this whole fix exists to remove.
  //
  // Guarded on a non-null profile so a read that failed here cannot blank a
  // good one the observer just set. Writing it twice with the same value is
  // harmless; writing null over a profile is not.
  const signIn = async (email: string, password: string): Promise<void> => {
    recordUserFlow(UserFlowMetrics.AUTH_FLOW_START, { type: 'signin' });

    try {
      const credential = await measureAsync('auth_signin', async () =>
        signInWithEmailAndPassword(auth, email, password)
      );

      const profile = await measureAsync('auth_load_profile', async () =>
        loadUserProfile(credential.user)
      );
      if (profile) {
        setUser(profile);
      }

      recordUserFlow(UserFlowMetrics.AUTH_FLOW_SUCCESS, { type: 'signin' });
    } catch (error) {
      console.error('Sign in error:', error);
      recordUserFlow(UserFlowMetrics.AUTH_FLOW_ERROR, {
        type: 'signin',
        error: error instanceof Error ? error.message : 'unknown',
      });
      throw error;
    }
  };

  // Sign up with email, password, and display name
  // Note: isLoading is NOT set at the start of this function. The SignUpPage
  // component manages its own loading state via TanStack Query (signUpMutation.isPending).
  // Setting isLoading here would cause PublicRoute to unmount SignUpPage and show
  // a loading spinner, which loses error state when the API call fails and SignUpPage
  // remounts as a fresh component. isLoading is only set to true after successful
  // sign-in, when we are transitioning to the authenticated state.
  const signUp = async (
    email: string,
    password: string,
    displayName: string,
    accessCode?: string
  ): Promise<void> => {
    recordUserFlow(UserFlowMetrics.AUTH_FLOW_START, { type: 'signup' });

    try {
      // Call backend signup endpoint (creates user via Admin SDK)
      const response = await measureAsync('auth_signup_api', async () =>
        fetch(`${appConfig.api.baseUrl}/v1/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            displayName,
            accessCode,
          }),
        })
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        // The status travels with the error so the sign-up page can recognise
        // the generic 403 refusal (#2077). The message is what it always was.
        throw new SignUpRequestError(
          errorBody.message || 'Signup failed. Please try again.',
          response.status
        );
      }

      // Sign in with the newly created credentials.
      //
      // The account exists from here on, and this sign-in is the first thing
      // that can fail for a reason that is not the user's: it runs the
      // `beforeUserSignedIn` blocking function, which Firebase gives 7 seconds,
      // and a cold start of that function has taken longer (#2123). So it is
      // tried twice. The first failure takes those 7 seconds, by which time
      // the instance has normally finished starting, and signing in twice is
      // safe — the trigger creates the profile idempotently.
      //
      // If the retry fails too, the error thrown says what is true: the
      // account was created. The raw Firebase error would be shown on the
      // sign-up form, where trying again is refused (the address is now in
      // use) and nothing tells the person they already have an account.
      const signInAfterSignup = () =>
        measureAsync('auth_signin_after_signup', async () =>
          signInWithEmailAndPassword(auth, email, password)
        );
      let credential: Awaited<ReturnType<typeof signInAfterSignup>>;
      try {
        credential = await signInAfterSignup();
      } catch {
        try {
          credential = await signInAfterSignup();
        } catch {
          // Deliberately not chained to the Firebase error as a `cause`: that
          // error can carry the address, and this one is logged below.
          throw new AccountCreatedSignInFailedError();
        }
      }

      // Ensure client-side displayName is consistent
      await measureAsync('auth_update_profile', async () =>
        updateProfile(credential.user, { displayName })
      );

      // Send email verification with custom domain redirect (PRD Section 5.2)
      try {
        const actionCodeSettings = createActionCodeSettings(email);
        await measureAsync('auth_send_verification', async () =>
          (sendEmailVerification as any)(credential.user, actionCodeSettings)
        );
      } catch (verificationError) {
        // Don't throw here - user is created, just email failed
        // We can still let them proceed and resend later
        console.error('Email verification failed:', verificationError);
      }

      // Deliberately NOT `setIsLoading(true)` here — the same race documented
      // above `signIn`, and this is where the pattern was copied from. The
      // observer already fired when `signInWithEmailAndPassword` resolved
      // above: it raised the flag, loaded the profile and lowered it again.
      // Raising it now leaves nothing to lower it, so the flag is stuck true
      // and `/verify-email` renders `ProtectedRoute`'s "Loading..." forever
      // until the user reloads by hand (#1951).
      //
      // The OIDC journey hid this for months because `OidcContinuePage` acts
      // on `firebaseUser` and navigates away before this line runs. An
      // ordinary sign-up has nothing to navigate it, so it just stops.

      // Set temporary user state while Cloud Functions create the profile
      // The beforeUserSignedIn trigger will create the Firestore document
      setUser({
        uid: credential.user.uid,
        email: credential.user.email,
        emailVerified: false,
        email_verified: false,
        display_name: displayName,
        display_name_source: DisplayNameSource.USER,
        created_at: new Date(),
        profile_completed: false,
      });

      // Mark as new user for onboarding flow
      setIsNewUser(true);

      recordUserFlow(UserFlowMetrics.AUTH_FLOW_SUCCESS, { type: 'signup' });
    } catch (error) {
      console.error('Sign up error:', error);
      recordUserFlow(UserFlowMetrics.AUTH_FLOW_ERROR, {
        type: 'signup',
        error: error instanceof Error ? error.message : 'unknown',
      });
      throw error;
    }
  };

  // Sign out
  const signOutUser = async (): Promise<void> => {
    try {
      await signOut(auth);
      setUser(null);
      setFirebaseUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  // Send email verification
  const sendVerificationEmail = async (): Promise<void> => {
    if (!firebaseUser) {
      throw new Error('No user signed in');
    }

    if (!firebaseUser.email) {
      throw new Error('User has no email address');
    }

    // Prevent email verification in PR previews to avoid unauthorized domain errors
    if (isPRPreviewEnvironment()) {
      throw new Error(
        'Email verification is not available in PR preview environments. Please test on dev.heapster.id'
      );
    }

    try {
      const actionCodeSettings = createActionCodeSettings(firebaseUser.email);
      await (sendEmailVerification as any)(firebaseUser, actionCodeSettings);
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  };

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser: FirebaseUser | null) => {
        setIsLoading(true);
        setFirebaseUser(firebaseUser);

        if (firebaseUser) {
          const profile = await loadUserProfile(firebaseUser);
          // Guarded for the reason spelled out above `refreshUserProfile`: a
          // `null` here means the READ FAILED, not that the person is signed
          // out, and writing it turns a transient Firestore blip into a silent
          // logout. This was the last unguarded null writer of the three —
          // `refreshUserProfile` and `signIn` already guard — and being inside
          // the observer made it the one that lands LAST, so it also blanked
          // the temporary profile `signUp` had just set, dropping a
          // brand-new account off `/verify-email` (#1951 review).
          if (profile) {
            setUser(profile);
          }
        } else {
          // Signed out is a different statement from a failed read, and this
          // branch is the only one entitled to make it.
          setUser(null);
        }

        setIsLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  // Context value
  const value: AuthContextType = {
    // State
    user,
    firebaseUser,
    isLoading,
    isEmailVerified: firebaseUser?.emailVerified || false,
    profileCompleted: user?.profile_completed || false,
    isNewUser,
    onboardingStep: getOnboardingStep(user, firebaseUser),
    // Actions
    signIn,
    signUp,
    signOut: signOutUser,
    sendVerificationEmail,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
